// Hooks for the follow-ups/calendar/marketing/members/suppliers/settings slice.
// The reads are mock-only until the desks and S11 land (live returns empty).
// The proposer is the ONE way these pages change anything: every button is a
// proposal through api.propose, and the backbone's answer is shown in words.
import { useCallback, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api, client, fetchImpl } from "../lib/api";
import { outcomeOfError, outcomeWords, type Outcome } from "../lib/proposal-outcome";
import { env } from "../lib/env";

type Row = Record<string, unknown>;

async function mockRead<T>(path: string, empty: T): Promise<T> {
  if (!env.mock) return empty;
  const r = await fetchImpl(`${env.apiBase}${path}`, { headers: { "x-aiw-surface": "crm" } });
  const j = (await r.json()) as { ok: true; data: T };
  return j.data;
}
const read = <T,>(key: string, path: string, empty: T) => useQuery({ queryKey: [key], queryFn: () => mockRead<T>(path, empty) });

// ── shapes (mock/slice-b.ts is the source; live rows will follow these) ──
export interface SiteVisit { id: string; job_ref: string | null; contact: string; suburb: string; who: string; at: string; note: string }
export interface InstallRow { id: string; job_ref: string; team: string; gate: string; next_date: string; days: { date: string; crew: string; task: string; done: string | null }[] }
export interface MemberRow { id: string; contact_id: string | null; name: string; email: string; phone: string; suburb: string; registered_at: string; homes: { kind: "measured" | "estimated"; area_m2: number | null; rooms: number }[]; self_quotes: number; bookings: string | null; consent: "ok" | "transactional" | "withdrawn"; consent_since: string; last_seen: string; last_seen_where: string }
export interface MemberTotals { members: number; this_week: number; self_quotes: number; self_quotes_became_leads: number; measured_homes: number; consent_pct: number }
export interface Campaign { id: string; name: string; source_id: string; channel: string; budget: string; period: string; state: string }
export interface WebsiteProduct { product: string; views: number; enquiries: number; quotes: number }
export interface PoReason { kind: "qty" | "stock" | "price" | "risk" | "match"; label: string; text: string }
export interface PoLine { id: string; item: string; dest: "job" | "inventory"; job_ref: string | null; who: string; qty: number; unit: string; unit_price: number; reasons: PoReason[] }
export interface PoDraft { ref: string; supplier_id: string; supplier: string; to: string; cc: string; from: string; deliver_to: string; deliver_by: string; drafted_at: string; cut_off: string; moq_note: string; pricelist: string; lines: PoLine[]; total_ex_gst: number }
export interface RecentPo { ref: string; supplier: string; for_job: string | null; total_ex_gst: number; eta: string; state: string; tone: "ok" | "warn" }
export interface ConfigMeta { kind: string; version: number; updated_by: string; updated_at: string }
export interface TouchesWeek { days: { day: string; done: number }[]; replies: number; of: number; booked: number }
export interface Snoozed { id: string; contact: string; suburb: string; note: string; until: string }

export const useSiteVisits = () => read<SiteVisit[]>("site-visits", "/api/mock/site-visits", []);
export const useInstalls = () => read<InstallRow[]>("installs-all", "/api/mock/installs", []);
export const useMembers = () => read<{ rows: MemberRow[]; totals: MemberTotals | null }>("members", "/api/mock/members", { rows: [], totals: null });
export const useCampaigns = () => read<Campaign[]>("campaigns", "/api/mock/campaigns", []);
export const useWebsiteProducts = () => read<WebsiteProduct[]>("website-products", "/api/mock/website-products", []);
export const usePoDraft = () => read<PoDraft | null>("po-draft", "/api/mock/po-draft", null);
export const useRecentPos = () => read<RecentPo[]>("recent-pos", "/api/mock/recent-pos", []);
export const useConfigMeta = () => read<ConfigMeta[]>("config-meta", "/api/mock/config-meta", []);
export const useTouchesWeek = () => read<TouchesWeek | null>("touches-week", "/api/mock/touches-week", null);
export const useSnoozed = () => read<Snoozed[]>("snoozed", "/api/mock/snoozed", []);

// ── proposals ──

export { outcomeWords, type Outcome } from "../lib/proposal-outcome";

/** Propose a command and keep the last answer per key (a row, a form). */
export function useProposer() {
  const [outcomes, setOutcomes] = useState<Record<string, Outcome>>({});
  const [busy, setBusy] = useState<Record<string, boolean>>({});
  const send = useCallback(async (key: string, command: string, args: Row, doneWord = "Done"): Promise<Outcome> => {
    setBusy((b) => ({ ...b, [key]: true }));
    try {
      const r = await api.propose(client, { command, args, idempotency_key: crypto.randomUUID() });
      const o = outcomeWords(r, doneWord);
      setOutcomes((m) => ({ ...m, [key]: o }));
      return o;
    } catch (e) {
      const o = outcomeOfError(e);
      setOutcomes((m) => ({ ...m, [key]: o }));
      return o;
    } finally {
      setBusy((b) => ({ ...b, [key]: false }));
    }
  }, []);
  const clear = useCallback((key: string) => setOutcomes((m) => { const n = { ...m }; delete n[key]; return n; }), []);
  return { send, outcomes, busy, clear };
}

export const OUTCOME_TONE: Record<Outcome["tone"], string> = {
  ok: "text-success",
  wait: "text-primary",
  no: "text-warning",
};
